import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// The only rule type available today is Field Mapping. New types get added to this union
// and to RULE_TYPE_OPTIONS below, and the panel branches on the selected value.
export type RuleType = 'field_mapping';

type RuleTypeSelectorProps = {
  value: RuleType;
  // Fired when the user picks an option from the menu — the panel advances to that rule's form.
  onSelect: (value: RuleType) => void;
};

const RuleTypeSelector = ({ value, onSelect }: RuleTypeSelectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const options: { value: RuleType; label: string }[] = [
    { value: 'field_mapping', label: t('Field Mapping') },
  ];
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  // Close the menu when clicking outside of it.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handlePick = (next: RuleType) => {
    setOpen(false);
    onSelect(next);
  };

  return (
    <div
      className="flex flex-col gap-2 rounded border border-unlisted-gray-lines bg-white p-4"
      data-testid="RuleTypeSelector"
    >
      <span className="text-sm font-semibold text-ui-text-primary-text">
        {t('Select rule type')}
      </span>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((isOpen) => !isOpen)}
          className="flex w-full items-center justify-between rounded border border-unlisted-gray-lines bg-white px-3 py-2 text-sm text-ui-text-primary-text transition-colors duration-200 hover:border-unlisted-medium-gray"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="RuleTypeSelector-toggle"
        >
          <span>{selected.label}</span>
          <span className="text-xs text-ui-text-primary-text">▾</span>
        </button>
        {open && (
          <ul
            role="listbox"
            className="absolute z-10 mt-1 w-full rounded border border-unlisted-gray-lines bg-white py-1 shadow-md"
            data-testid="RuleTypeSelector-menu"
          >
            {options.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
              >
                <button
                  type="button"
                  onClick={() => handlePick(option.value)}
                  className="w-full px-3 py-2 text-left text-sm text-ui-text-primary-text transition-colors duration-200 hover:bg-unlisted-light-gray"
                  data-testid={`RuleTypeSelector-option-${option.value}`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RuleTypeSelector;
